import { Fragment, useEffect } from "react";
import "lenis/dist/lenis.css";
import "@/App.css";
import Lenis from "lenis";
import { MotionConfig } from "framer-motion";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { setLenis, scrollToTop } from "@/lib/scroll";
import { LangProvider, useContent, useLang } from "@/i18n/LangContext";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import Home from "@/pages/Home";
import PublicationIndex from "@/pages/PublicationIndex";
import LocalPolicy from "@/pages/LocalPolicy";
import Impressum from "@/pages/Impressum";
import Datenschutz from "@/pages/Datenschutz";
import Article from "@/pages/Article";
import { AuthProvider } from "@/portal/AuthContext";
import { RequireRole } from "@/portal/Shell";
import Login from "@/portal/pages/Login";
import Register from "@/portal/pages/Register";
import ClientDashboard from "@/portal/pages/ClientDashboard";
import ManagedWorkspace from "@/portal/pages/ManagedWorkspace";
import NewTicket from "@/portal/pages/NewTicket";
import ClientTicket from "@/portal/pages/ClientTicket";
import Profile from "@/portal/pages/Profile";
import AdminDashboard from "@/portal/pages/AdminDashboard";
import AdminTicket from "@/portal/pages/AdminTicket";
import { AdminClients, AdminContacts, AdminTickets } from "@/portal/pages/AdminLists";
import SiteEditor from "@/site-editor/SiteEditor";
import WorkspaceEditor from "@/site-editor/WorkspaceEditor";
import SiteCanvas, {CustomSitePage} from "@/site-editor/SiteCanvas";

const client = (el) => <RequireRole role="client">{el}</RequireRole>;
const admin = (el) => <RequireRole role="admin">{el}</RequireRole>;

const Chrome = ({ children }) => {
  const { pathname } = useLocation();
  const {lang} = useLang();
  const portal = pathname.startsWith("/kundenbereich") || pathname.startsWith("/admin");
  useEffect(() => {
    if (portal) scrollToTop();
  }, [pathname, portal]);
  if (portal) return <main id="main" tabIndex={-1}>{children}</main>;
  return (
    <Fragment key={`${pathname}:${lang}`}>
      <SkipLink />
      <Header />
      <main id="main" tabIndex={-1}>{children}</main>
      <Footer />
    </Fragment>
  );
};

const useSmoothScroll = () => {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    setLenis(lenis);
    let raf = requestAnimationFrame(function loop(t) {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    });
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      setLenis(null);
    };
  }, []);
};

const NotFound = () => {
  const { ui } = useContent();
  return (
  <div className="mx-auto max-w-[1440px] px-5 pb-32 pt-[160px] sm:px-8 lg:px-12">
    <p className="eyebrow">404</p>
    <h1 className="mt-6 font-display text-5xl font-extrabold tracking-[-0.04em]">{ui.notFound.title}</h1>
    <Link to="/" className="mt-8 inline-block border-b border-ink pb-1 font-medium hover:text-signal" data-testid="notfound-home-link">{ui.notFound.back}</Link>
  </div>
  );
};

const SkipLink = () => {
  const { ui } = useContent();
  return (
    <a href="#main" className="sr-only z-[70] bg-ink px-4 py-3 text-paper focus:not-sr-only focus:fixed focus:left-4 focus:top-4" data-testid="skip-link">
      {ui.skip}
    </a>
  );
};

function App() {
  useSmoothScroll();
  return (
    <LangProvider>
    <AuthProvider>
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <div className="grain min-h-screen">
          <Chrome>
            <SiteCanvas />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/datenschutz" element={<Datenschutz />} />
              <Route path="/publikationen" element={<PublicationIndex />} />
              <Route path="/cookies-policy" element={<LocalPolicy kind="cookies" />} />
              <Route path="/disclaimer" element={<LocalPolicy kind="disclaimer" />} />
              <Route path="/publikationen/:slug" element={<Article />} />
              <Route path="/seiten/:slug" element={<CustomSitePage />} />
              <Route path="/kundenbereich/anmelden" element={<Login />} />
              <Route path="/kundenbereich/registrieren" element={<Register />} />
              <Route path="/kundenbereich" element={<RequireRole role="client" bare><ManagedWorkspace /></RequireRole>} />
              <Route path="/kundenbereich/anfragen" element={client(<ClientDashboard />)} />
              <Route path="/kundenbereich/anfragen/neu" element={client(<NewTicket />)} />
              <Route path="/kundenbereich/anfragen/:id" element={client(<ClientTicket />)} />
              <Route path="/kundenbereich/profil" element={client(<Profile />)} />
              <Route path="/admin" element={admin(<AdminDashboard />)} />
              <Route path="/admin/profil" element={admin(<Profile />)} />
              <Route path="/admin/site-editor" element={<RequireRole role="admin" bare><SiteEditor /></RequireRole>} />
              <Route path="/admin/workspace-editor" element={<RequireRole role="admin" bare><WorkspaceEditor /></RequireRole>} />
              <Route path="/admin/workspace" element={<RequireRole role="admin" bare><ManagedWorkspace admin /></RequireRole>} />
              <Route path="/admin/anfragen" element={admin(<AdminTickets />)} />
              <Route path="/admin/anfragen/:id" element={admin(<AdminTicket />)} />
              <Route path="/admin/kunden" element={admin(<AdminClients />)} />
              <Route path="/admin/kontaktanfragen" element={admin(<AdminContacts />)} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Chrome>
        </div>
      </BrowserRouter>
    </MotionConfig>
    </AuthProvider>
    </LangProvider>
  );
}

export default App;
